import { DialogComponent } from '@theme/dialog';

const INSTALLMENTS_DRAWER_OPEN = 'installments-drawer-open';

/**
 * Opens an installments drawer by provider (flexipay | clearpay).
 * Dispatch from product-installments "Show more" / "Learn more" click.
 *
 * @typedef {{ provider: 'flexipay' | 'clearpay' }} InstallmentsDrawerOpenDetail
 * @extends CustomEvent<InstallmentsDrawerOpenDetail>
 */
export class InstallmentsDrawerOpenEvent extends CustomEvent {
  /** @param {InstallmentsDrawerOpenDetail} detail */
  constructor(detail) {
    super(INSTALLMENTS_DRAWER_OPEN, { detail, bubbles: true });
  }
}

/**
 * Single panel (Flexi Pay or Clearpay). Extends DialogComponent; opens via host event.
 */
class InstallmentsDrawerPanel extends DialogComponent {
  requiredRefs = ['dialog'];

  /**
   * Close button handler (on:click="installments-drawer-panel/close").
   * @param {Event} event
   */
  close(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  }
}

/**
 * Host that contains both panels and opens the correct one when event fires.
 */
class InstallmentsDrawerHost extends HTMLElement {
  connectedCallback() {
    document.addEventListener(INSTALLMENTS_DRAWER_OPEN, this.#handleOpen);
  }

  disconnectedCallback() {
    document.removeEventListener(INSTALLMENTS_DRAWER_OPEN, this.#handleOpen);
  }

  /** @param {InstallmentsDrawerOpenEvent} event */
  #handleOpen = (event) => {
    const provider = event.detail?.provider;
    if (!provider) return;
    const panel = this.querySelector(`installments-drawer-panel[data-provider="${provider}"]`);
    if (panel instanceof InstallmentsDrawerPanel) {
      panel.showDialog();
    }
  };
}

if (!customElements.get('installments-drawer-panel')) {
  customElements.define('installments-drawer-panel', InstallmentsDrawerPanel);
}
if (!customElements.get('installments-drawer-host')) {
  customElements.define('installments-drawer-host', InstallmentsDrawerHost);
}
